import { useState, useRef, useCallback, useEffect, lazy, Suspense } from "react";
import { Capacitor } from "@capacitor/core";
import { Button } from "@/components/ui/button";
import { Camera, Loader2, Image, Download, Save, X, Trash2, RotateCcw, Check, Move, Sun, Contrast, Palette, Share2, Copy, ExternalLink, MessageSquare, Eye, Crop } from "lucide-react";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { toast } from "sonner";
import { useUsageLimits } from "@/hooks/useUsageLimits";
import { UsageLimitBanner } from "@/components/usage/UsageLimitBanner";
import { supabase } from "@/integrations/supabase/client";
import { detectDocumentEdges } from "./edgeDetection";
import { applyPerspectiveCorrection } from "./perspectiveCorrection";
import { FilterType, getFilterLabel, applyFilterToBlob } from "./imageFilters";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { getShareUrl } from "@/lib/shareUrl";
import { useNativeScanner } from "@/hooks/useNativeScanner";

// Lazy load OpenCVScanner for better initial load
const OpenCVScanner = lazy(() => import('./OpenCVScanner'));

interface ScannedPage {
  id: string;
  blob: Blob;
  preview: string;
}

interface Point {
  x: number;
  y: number;
}

// Mobile-optimized sizes
const MAGNIFIER_SIZE = 80; // Smaller for less obstruction
const MAGNIFIER_ZOOM = 2; // Clear zoom without being overwhelming
const HANDLE_SIZE = 44; // iOS minimum touch target
const HANDLE_HIT_AREA = 56; // Extended touch area

const revokeBlobUrl = (maybeUrl: string | null) => {
  if (maybeUrl && maybeUrl.startsWith("blob:")) {
    try {
      URL.revokeObjectURL(maybeUrl);
    } catch {
      // ignore
    }
  }
};

const DocumentScanner = () => {
  const { t } = useTranslation();
  const [pages, setPages] = useState<ScannedPage[]>([]);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [originalCapturedImage, setOriginalCapturedImage] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
  const [croppedPreview, setCroppedPreview] = useState<string | null>(null);
  const [originalCapturedBlob, setOriginalCapturedBlob] = useState<Blob | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<FilterType>("original");
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [detecting, setDetecting] = useState(false);
  const [shareDialogUrl, setShareDialogUrl] = useState<string | null>(null);
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<'read' | 'read_comment'>('read');
  const [pendingShareData, setPendingShareData] = useState<{ docId: string; userId: string; doc: any } | null>(null);
  
  // Cropping state
  const [corners, setCorners] = useState<Point[]>([]);
  const [draggingCorner, setDraggingCorner] = useState<number | null>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [displayScale, setDisplayScale] = useState(1);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [magnifierPos, setMagnifierPos] = useState<Point | null>(null);
  
  // Pinch-to-zoom state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPinching, setIsPinching] = useState(false);
  const lastPinchDistanceRef = useRef<number | null>(null);
  const lastPanPosRef = useRef<Point | null>(null);
  const lastPointerPosRef = useRef<Point | null>(null);
  
  const { checkLimit, incrementUsage, limits } = useUsageLimits();
  const {
    platform: nativePlatform,
    isNativePlatform,
    isNativeAvailable,
    isLoading: isNativeLoading,
    lastError: nativeLastError,
    recheckAvailability,
    scanDocument: nativeScan,
  } = useNativeScanner();

  const isIosNative = isNativePlatform && nativePlatform === "ios";
  const isRunningInWebPreview = Capacitor.getPlatform() === "web";

  // Calculate max pages based on plan
  const maxPagesPerDoc = limits.scanner_pages_per_doc;
  const canAddMorePages = maxPagesPerDoc === -1 || pages.length < maxPagesPerDoc;
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);
  const magnifierCanvasRef = useRef<HTMLCanvasElement>(null);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      const video = videoRef.current;

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      if (video) {
        video.pause();
        video.srcObject = null;
      }
    };
  }, []);

  // Connect stream to video element when capturing starts
  useEffect(() => {
    if (!isCapturing || !streamRef.current) return;
    
    const video = videoRef.current;
    if (video && streamRef.current) {
      video.srcObject = streamRef.current;
      video.play().catch(() => {
        // ignore autoplay restrictions errors on some browsers
      });
    }

    // Cleanup when capturing stops
    return () => {
      if (video) {
        video.srcObject = null;
      }
    };
  }, [isCapturing]);

  const startCamera = async () => {
    // Use native iOS scanner if available (VisionKit)
    if (isNativeAvailable) {
      try {
        const result = await nativeScan();
        if (!result) return; // User cancelled

        // Process each scanned page
        for (const base64Image of result.base64Images) {
          // Check page limit
          if (!canAddMorePages) {
            toast.error(`Limite de ${maxPagesPerDoc} pages atteinte.`);
            break;
          }

          // Convert base64 to blob
          const response = await fetch(base64Image);
          const blob = await response.blob();

          // Add page directly (native scanner already crops perfectly)
          const pageId = `page-${Date.now()}-${Math.random()}`;
          const preview = URL.createObjectURL(blob);
          setPages((prev) => [...prev, { id: pageId, blob, preview }]);
        }

        if (result.pageCount > 0) {
          toast.success(
            `${result.pageCount} page${result.pageCount > 1 ? "s" : ""} scannée${
              result.pageCount > 1 ? "s" : ""
            }`
          );
        }
      } catch (error) {
        console.error("Native scanner error:", error);
        toast.error("Erreur lors du scan natif");
      }
      return;
    }

    // If we're on a native iPhone app but native scanner isn't detected, 
    // use file picker fallback instead of OpenCV (which requires CDN)
    if (isIosNative) {
      if (!isNativeLoading) {
        toast.info("Scanner natif non disponible. Utilisez la sélection de fichier.");
      }
      // Trigger file input instead
      const fileInput = document.getElementById('scanner-image-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.click();
      }
      return;
    }

    // Fallback for web: Camera with OpenCV
    try {
      setCameraError(null);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 2560 },
          aspectRatio: { ideal: 3/4 }
        },
      });

      streamRef.current = stream;
      setIsCapturing(true);
    } catch (error) {
      console.error("Camera error:", error);
      setCameraError("Impossible d'accéder à la caméra. Vérifiez les permissions.");
      toast.error("Impossible d'accéder à la caméra");
    }
  };

  const stopCamera = useCallback(() => {
    const video = videoRef.current;

    if (streamRef.current) {
      // Stop all tracks from the stream
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (video) {
      // Detach the stream from the video element (important for Safari)
      video.pause();
      video.srcObject = null;
    }

    setIsCapturing(false);
  }, []);

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      toast.error("La caméra n'est pas encore prête");
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (video.videoWidth === 0 || video.videoHeight === 0) {
      toast.error("Patientez une seconde puis réessayez");
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setCapturedImage(dataUrl);
    setOriginalCapturedImage(dataUrl);
    setIsEditing(true);
    stopCamera();
    
    // Load image and detect edges
    setDetecting(true);
    const img = new window.Image();
    img.onload = () => {
      imageRef.current = img;
      setImageSize({ width: img.width, height: img.height });
      
      const detectedCorners = detectDocumentEdges(img);
      setCorners(detectedCorners);
      setDetecting(false);
    };
    img.src = dataUrl;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      setCapturedImage(dataUrl);
      setOriginalCapturedImage(dataUrl);
      setIsEditing(true);
      
      // Load image and detect edges
      setDetecting(true);
      const img = new window.Image();
      img.onload = () => {
        imageRef.current = img;
        setImageSize({ width: img.width, height: img.height });
        
        const detectedCorners = detectDocumentEdges(img);
        setCorners(detectedCorners);
        setDetecting(false);
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Calculate display scale when container or image changes (accounting for object-cover)
  useEffect(() => {
    if (!isEditing || !cropContainerRef.current || imageSize.width === 0) return;
    
    const container = cropContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerW = containerRect.width;
    const containerH = containerRect.height;
    
    setContainerSize({ width: containerW, height: containerH });
    
    const containerRatio = containerW / containerH;
    const imageRatio = imageSize.width / imageSize.height;
    
    let scale: number;
    let offsetX = 0;
    let offsetY = 0;
    
    // object-cover: scale to fill, may crop
    if (imageRatio > containerRatio) {
      // Landscape image in portrait container: scale by height, crop width
      scale = containerH / imageSize.height;
      const scaledWidth = imageSize.width * scale;
      offsetX = (containerW - scaledWidth) / 2; // negative offset (image extends beyond container)
    } else {
      // Portrait image in portrait container: scale by width, crop height
      scale = containerW / imageSize.width;
      const scaledHeight = imageSize.height * scale;
      offsetY = (containerH - scaledHeight) / 2; // negative offset
    }
    
    setDisplayScale(scale);
    setImageOffset({ x: offsetX, y: offsetY });
  }, [isEditing, imageSize]);

  // Update magnifier when dragging
  const updateMagnifier = useCallback((cornerX: number, cornerY: number) => {
    const canvas = magnifierCanvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = MAGNIFIER_SIZE;
    canvas.height = MAGNIFIER_SIZE;

    // Calculate the source area to zoom
    const sourceSize = MAGNIFIER_SIZE / MAGNIFIER_ZOOM;
    const sourceX = cornerX - sourceSize / 2;
    const sourceY = cornerY - sourceSize / 2;

    // Clear and draw zoomed portion
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, MAGNIFIER_SIZE, MAGNIFIER_SIZE);
    
    ctx.drawImage(
      img,
      sourceX, sourceY, sourceSize, sourceSize,
      0, 0, MAGNIFIER_SIZE, MAGNIFIER_SIZE
    );

    // Draw crosshair
    ctx.strokeStyle = 'hsl(var(--primary))';
    ctx.lineWidth = 2;
    
    // Horizontal line
    ctx.beginPath();
    ctx.moveTo(MAGNIFIER_SIZE / 2 - 15, MAGNIFIER_SIZE / 2);
    ctx.lineTo(MAGNIFIER_SIZE / 2 + 15, MAGNIFIER_SIZE / 2);
    ctx.stroke();
    
    // Vertical line
    ctx.beginPath();
    ctx.moveTo(MAGNIFIER_SIZE / 2, MAGNIFIER_SIZE / 2 - 15);
    ctx.lineTo(MAGNIFIER_SIZE / 2, MAGNIFIER_SIZE / 2 + 15);
    ctx.stroke();

    // Center dot
    ctx.fillStyle = 'hsl(var(--primary))';
    ctx.beginPath();
    ctx.arc(MAGNIFIER_SIZE / 2, MAGNIFIER_SIZE / 2, 4, 0, Math.PI * 2);
    ctx.fill();
  }, []);

  const handlePointerDown = (index: number, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingCorner(index);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    
    if (!cropContainerRef.current) return;
    const rect = cropContainerRef.current.getBoundingClientRect();
    const pointerX = e.clientX - rect.left;
    const pointerY = e.clientY - rect.top;
    lastPointerPosRef.current = { x: pointerX, y: pointerY };
    
    // Initialize magnifier position at corner display position
    const corner = corners[index];
    if (corner) {
      const displayX = corner.x * displayScale + imageOffset.x;
      const displayY = corner.y * displayScale + imageOffset.y;
      // Clamp to container bounds
      const clampedDisplayX = Math.max(0, Math.min(containerSize.width, displayX));
      const clampedDisplayY = Math.max(0, Math.min(containerSize.height, displayY));
      setMagnifierPos({ x: clampedDisplayX, y: clampedDisplayY });
      updateMagnifier(corner.x, corner.y);
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (draggingCorner === null || !cropContainerRef.current || isPinching) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const rect = cropContainerRef.current.getBoundingClientRect();
    const pointerX = e.clientX - rect.left;
    const pointerY = e.clientY - rect.top;
    
    if (!lastPointerPosRef.current) {
      lastPointerPosRef.current = { x: pointerX, y: pointerY };
      return;
    }
    
    const deltaDisplayX = pointerX - lastPointerPosRef.current.x;
    const deltaDisplayY = pointerY - lastPointerPosRef.current.y;
    const effectiveScale = displayScale * zoomLevel || 1;
    const deltaImageX = deltaDisplayX / effectiveScale;
    const deltaImageY = deltaDisplayY / effectiveScale;
    
    const currentCorner = corners[draggingCorner];
    if (!currentCorner) return;
    
    const unclampedX = currentCorner.x + deltaImageX;
    const unclampedY = currentCorner.y + deltaImageY;
    const clampedX = Math.max(0, Math.min(imageSize.width, unclampedX));
    const clampedY = Math.max(0, Math.min(imageSize.height, unclampedY));
    
    setCorners(prev => {
      if (prev.length !== 4) return prev;
      return prev.map((corner, i) => 
        i === draggingCorner ? { x: clampedX, y: clampedY } : corner
      );
    });
    
    lastPointerPosRef.current = { x: pointerX, y: pointerY };
    const displayX = Math.max(0, Math.min(rect.width, pointerX));
    const displayY = Math.max(0, Math.min(rect.height, pointerY));
    setMagnifierPos({ x: displayX, y: displayY });
    updateMagnifier(clampedX, clampedY);
  };

  const handlePointerUp = () => {
    setDraggingCorner(null);
    setMagnifierPos(null);
    lastPointerPosRef.current = null;
  };

  // Pinch-to-zoom handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Start pinch
      e.preventDefault();
      setIsPinching(true);
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      lastPinchDistanceRef.current = distance;
      
      // Calculate center point for panning
      const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      lastPanPosRef.current = { x: centerX, y: centerY };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && isPinching && cropContainerRef.current) {
      e.preventDefault();
      
      // Calculate new distance
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      
      if (lastPinchDistanceRef.current !== null) {
        const scale = distance / lastPinchDistanceRef.current;
        setZoomLevel(prev => Math.max(1, Math.min(4, prev * scale)));
      }
      lastPinchDistanceRef.current = distance;
      
      // Handle panning
      const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      
      if (lastPanPosRef.current) {
        const deltaX = centerX - lastPanPosRef.current.x;
        const deltaY = centerY - lastPanPosRef.current.y;
        setPanOffset(prev => ({
          x: prev.x + deltaX,
          y: prev.y + deltaY
        }));
      }
      lastPanPosRef.current = { x: centerX, y: centerY };
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      setIsPinching(false);
      lastPinchDistanceRef.current = null;
      lastPanPosRef.current = null;
    }
  };

  const resetZoom = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const resetCorners = () => {
    if (!imageRef.current) return;
    setDetecting(true);
    const detectedCorners = detectDocumentEdges(imageRef.current);
    setCorners(detectedCorners);
    setDetecting(false);
  };

  const confirmCrop = async () => {
    if (!imageRef.current || corners.length !== 4) return;
    
    setDetecting(true);
    try {
      const blob = await applyPerspectiveCorrection(imageRef.current, corners);
      const preview = URL.createObjectURL(blob);
      
      // Go to filter step instead of adding page directly
      setCroppedBlob(blob);
      setCroppedPreview(preview);
      setSelectedFilter("original");
      setIsEditing(false);
      setIsFiltering(true);
      setCorners([]);
      // keep captured image around for backToCrop
      // NOTE: if it was a blob URL, it will be cleaned up when leaving the flow
      imageRef.current = null;
      resetZoom();
    } catch (error) {
      console.error("Crop error:", error);
      toast.error("Erreur lors du recadrage");
    } finally {
      setDetecting(false);
    }
  };

  const confirmFilter = async () => {
    if (!croppedBlob) return;
    
    // Check page limit before adding
    if (!canAddMorePages) {
      toast.error(`Limite de ${maxPagesPerDoc} pages atteinte. Passez à un forfait supérieur.`);
      cancelFilter();
      return;
    }
    
    setDetecting(true);
    try {
      const filteredBlob = await applyFilterToBlob(croppedBlob, selectedFilter);
      const pageId = `page-${Date.now()}`;
      const preview = URL.createObjectURL(filteredBlob);
      setPages(prev => [...prev, { id: pageId, blob: filteredBlob, preview }]);
      toast.success(`Page ${pages.length + 1} ajoutée`);
      
      // Clean up
      if (croppedPreview) URL.revokeObjectURL(croppedPreview);
      setCroppedBlob(null);
      setCroppedPreview(null);
      setIsFiltering(false);
      setCapturedImage(null);
    } catch (error) {
      console.error("Filter error:", error);
      toast.error("Erreur lors de l'application du filtre");
    } finally {
      setDetecting(false);
    }
  };

  const cancelFilter = () => {
    if (croppedPreview) URL.revokeObjectURL(croppedPreview);
    revokeBlobUrl(capturedImage);
    revokeBlobUrl(originalCapturedImage);
    setCroppedBlob(null);
    setCroppedPreview(null);
    setOriginalCapturedBlob(null);
    setIsFiltering(false);
    setCapturedImage(null);
    setOriginalCapturedImage(null);
  };

  const backToCrop = () => {
    if (!originalCapturedImage) return;
    
    // Clean up filter state
    if (croppedPreview) URL.revokeObjectURL(croppedPreview);
    setCroppedBlob(null);
    setCroppedPreview(null);
    setIsFiltering(false);
    
    // Restore to crop mode with original image
    setCapturedImage(originalCapturedImage);
    setIsEditing(true);
    
    // Re-detect edges
    setDetecting(true);
    const img = new window.Image();
    img.onload = () => {
      imageRef.current = img;
      setImageSize({ width: img.width, height: img.height });
      const detectedCorners = detectDocumentEdges(img);
      setCorners(detectedCorners);
      setDetecting(false);
    };
    img.src = originalCapturedImage;
  };

  // Auto-recrop: re-run edge detection and perspective correction on original image
  const autoRecrop = async () => {
    if (!originalCapturedBlob) {
      toast.error("Image originale non disponible");
      return;
    }

    setDetecting(true);
    try {
      const url = URL.createObjectURL(originalCapturedBlob);
      const img = new window.Image();
      
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = url;
      });

      // Detect edges
      const detectedCorners = detectDocumentEdges(img);
      
      if (detectedCorners.length === 4) {
        // Apply perspective correction
        const correctedBlob = await applyPerspectiveCorrection(img, detectedCorners);
        const correctedUrl = URL.createObjectURL(correctedBlob);
        
        // Clean up old preview
        if (croppedPreview) URL.revokeObjectURL(croppedPreview);
        
        setCroppedBlob(correctedBlob);
        setCroppedPreview(correctedUrl);
        toast.success("Document recadré automatiquement");
      } else {
        toast.error("Impossible de détecter les bords du document");
      }
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Auto-recrop error:", error);
      toast.error("Erreur lors du recadrage automatique");
    } finally {
      setDetecting(false);
    }
  };

  const cancelEdit = () => {
    revokeBlobUrl(capturedImage);
    revokeBlobUrl(originalCapturedImage);
    setCapturedImage(null);
    setOriginalCapturedImage(null);
    setIsEditing(false);
    setCorners([]);
    imageRef.current = null;
    resetZoom();
  };

  const removePage = (pageId: string) => {
    setPages(prev => {
      const updated = prev.filter(p => p.id !== pageId);
      const removed = prev.find(p => p.id === pageId);
      if (removed) URL.revokeObjectURL(removed.preview);
      return updated;
    });
  };

  const movePage = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= pages.length) return;
    setPages(prev => {
      const newPages = [...prev];
      const [movedPage] = newPages.splice(fromIndex, 1);
      newPages.splice(toIndex, 0, movedPage);
      return newPages;
    });
  };

  const resetAll = () => {
    pages.forEach(p => URL.revokeObjectURL(p.preview));
    revokeBlobUrl(capturedImage);
    revokeBlobUrl(originalCapturedImage);
    setPages([]);
    setCapturedImage(null);
    setOriginalCapturedImage(null);
    setIsEditing(false);
    setIsFiltering(false);
    setCorners([]);
  };

  const downloadAsPdf = async () => {
    if (pages.length === 0) return;

    if (!checkLimit("pdf_actions")) {
      toast.error("Limite d'actions PDF atteinte");
      return;
    }

    setProcessing(true);
    
    try {
      if (pages.length === 1) {
        const formData = new FormData();
        formData.append('image', pages[0].blob, 'scan.jpg');

        const { data, error } = await supabase.functions.invoke('convert-to-pdf', {
          body: formData,
        });

        if (error) throw error;

        const blob = new Blob([data], { type: 'application/pdf' });
        downloadBlob(blob, `scan-${Date.now()}.pdf`);
      } else {
        const pdfBlobs: ArrayBuffer[] = [];
        
        for (const page of pages) {
          const formData = new FormData();
          formData.append('image', page.blob, 'scan.jpg');

          const { data, error } = await supabase.functions.invoke('convert-to-pdf', {
            body: formData,
          });

          if (error) throw error;
          pdfBlobs.push(data);
        }

        const mergeFormData = new FormData();
        pdfBlobs.forEach((pdfBuffer, i) => {
          const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
          mergeFormData.append('pdfs', blob, `page-${i}.pdf`);
        });

        const { data: mergedData, error: mergeError } = await supabase.functions.invoke('merge-pdfs', {
          body: mergeFormData,
        });

        if (mergeError) throw mergeError;

        const mergedBlob = new Blob([mergedData], { type: 'application/pdf' });
        downloadBlob(mergedBlob, `scan-${Date.now()}.pdf`);
      }

      await incrementUsage("pdf_actions");
      toast.success("PDF téléchargé !");
    } catch (error) {
      console.error('Error converting to PDF:', error);
      toast.error("Erreur lors de la conversion");
    } finally {
      setProcessing(false);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const saveAsDocument = async () => {
    if (pages.length === 0) return;

    if (!checkLimit("pdf_actions")) {
      toast.error("Limite d'actions PDF atteinte");
      return;
    }

    setSaving(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté");
        setSaving(false);
        return;
      }

      let finalPdfBlob: Blob;

      if (pages.length === 1) {
        const formData = new FormData();
        formData.append('image', pages[0].blob, 'scan.jpg');

        const { data: pdfData, error: convertError } = await supabase.functions.invoke('convert-to-pdf', {
          body: formData,
        });

        if (convertError) throw convertError;
        finalPdfBlob = new Blob([pdfData], { type: 'application/pdf' });
      } else {
        const pdfBlobs: ArrayBuffer[] = [];
        
        for (const page of pages) {
          const formData = new FormData();
          formData.append('image', page.blob, 'scan.jpg');

          const { data, error } = await supabase.functions.invoke('convert-to-pdf', {
            body: formData,
          });

          if (error) throw error;
          pdfBlobs.push(data);
        }

        const mergeFormData = new FormData();
        pdfBlobs.forEach((pdfBuffer, i) => {
          const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
          mergeFormData.append('pdfs', blob, `page-${i}.pdf`);
        });

        const { data: mergedData, error: mergeError } = await supabase.functions.invoke('merge-pdfs', {
          body: mergeFormData,
        });

        if (mergeError) throw mergeError;
        finalPdfBlob = new Blob([mergedData], { type: 'application/pdf' });
      }

      const fileName = `scan-${Date.now()}.pdf`;
      const filePath = `${user.id}/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, finalPdfBlob, {
          contentType: 'application/pdf',
        });

      if (uploadError) throw uploadError;

      const pageLabel = pages.length > 1 ? ` (${pages.length} pages)` : '';
      const docTitle = `Document scanné ${new Date().toLocaleDateString('fr-FR')}${pageLabel}`;
      
      // Call validate-document instead of direct insert
      const { data: validateResult, error: validateError } = await supabase.functions.invoke('validate-document', {
        body: {
          fileName,
          fileSize: finalPdfBlob.size,
          mimeType: 'application/pdf',
          filePath,
          source: 'upload',
          userId: user.id,
          title: docTitle,
          sourceMetadata: {
            scanner_pages: pages.length,
            scanner_type: isNativeAvailable ? 'native_ios' : 'web_opencv',
          },
        },
      });

      if (validateError) throw validateError;

      if (!validateResult?.success || validateResult?.result === 'rejected') {
        // Cleanup uploaded file
        await supabase.storage.from('documents').remove([filePath]);
        toast.error(validateResult?.reason || "Document rejeté par le firewall");
        setSaving(false);
        return;
      }

      const documentId = validateResult.documentId;

      await incrementUsage("pdf_actions");
      toast.success("Document sauvegardé !");
      
      // Reset UI immediately after successful save
      setSaving(false);
      resetAll();

      // Trigger classification in background with required params
      supabase.functions.invoke('classify-document-v2', {
        body: { 
          documentId,
          filePath,
          fileName,
          mimeType: 'application/pdf'
        }
      }).catch(err => console.error('Classification error:', err));

      supabase.functions.invoke('generate-document-actions', {
        body: { documentId }
      }).catch(err => console.error('Actions generation error:', err));
      
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error("Erreur lors de la sauvegarde");
      setSaving(false);
    }
  };

  // Render cropping overlay
  const renderCropOverlay = () => {
    if (corners.length !== 4) return null;
    
    const scaledCorners = corners.map(c => {
      const displayX = c.x * displayScale + imageOffset.x;
      const displayY = c.y * displayScale + imageOffset.y;
      return {
        x: containerSize.width ? Math.max(0, Math.min(containerSize.width, displayX)) : displayX,
        y: containerSize.height ? Math.max(0, Math.min(containerSize.height, displayY)) : displayY,
      };
    });
    
    // Create SVG path for the selection area
    const pathD = `M ${scaledCorners[0].x} ${scaledCorners[0].y} 
                   L ${scaledCorners[1].x} ${scaledCorners[1].y} 
                   L ${scaledCorners[2].x} ${scaledCorners[2].y} 
                   L ${scaledCorners[3].x} ${scaledCorners[3].y} Z`;
    
    return (
      <svg 
        className="absolute inset-0 w-full h-full pointer-events-none"
      >
        {/* Dark overlay outside selection */}
        <defs>
          <mask id="cropMask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <path d={pathD} fill="black" />
          </mask>
        </defs>
        <rect 
          x="0" y="0" 
          width="100%" height="100%" 
          fill="rgba(0,0,0,0.6)" 
          mask="url(#cropMask)" 
        />
        
        {/* Selection border */}
        <path 
          d={pathD} 
          fill="none" 
          stroke="hsl(var(--primary))" 
          strokeWidth="3" 
        />
        
        {/* Corner lines for visual polish */}
        {scaledCorners.map((corner, i) => {
          const next = scaledCorners[(i + 1) % 4];
          return (
            <line 
              key={`line-${i}`}
              x1={corner.x} y1={corner.y}
              x2={next.x} y2={next.y}
              stroke="hsl(var(--primary))"
              strokeWidth="2"
            />
          );
        })}
      </svg>
    );
  };

  // Calculate magnifier position - always visible, offset from finger
  const getMagnifierStyle = (): React.CSSProperties => {
    if (!magnifierPos || !cropContainerRef.current) return { display: 'none' };

    const containerRect = cropContainerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    // Position above finger by default, with offset to avoid finger obstruction
    const offsetY = 60; // Distance above the touch point
    let x = magnifierPos.x;
    let y = magnifierPos.y - offsetY - MAGNIFIER_SIZE / 2;

    // If too close to top, place below instead
    if (y < MAGNIFIER_SIZE / 2 + 8) {
      y = magnifierPos.y + offsetY + MAGNIFIER_SIZE / 2;
    }

    // Clamp horizontally to stay inside container
    const halfSize = MAGNIFIER_SIZE / 2;
    x = Math.max(halfSize + 4, Math.min(containerWidth - halfSize - 4, x));
    
    // Clamp vertically
    y = Math.max(halfSize + 4, Math.min(containerHeight - halfSize - 4, y));

    return {
      left: x,
      top: y,
      transform: 'translate(-50%, -50%)',
    };
  };

  return (
    <div className="space-y-4">
      <UsageLimitBanner type="pdf_actions" showUpgrade={false} />
      
      <canvas ref={canvasRef} className="hidden" />

      {/* Cropping view with edge detection */}
      {isEditing && capturedImage && (
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-2">
            <div className="text-center text-sm font-medium py-2 px-4 rounded-lg bg-primary/10 text-primary">
              {detecting ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Détection...
                </span>
              ) : (
                <span className="text-xs">Pincez pour zoomer</span>
              )}
            </div>
            {zoomLevel > 1 && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={resetZoom}
                className="shrink-0"
              >
                {Math.round(zoomLevel * 100)}%
              </Button>
            )}
          </div>
          
          <div 
            ref={cropContainerRef}
            className="relative bg-muted rounded-2xl overflow-hidden aspect-[3/4]"
            style={{ touchAction: 'none' }}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="w-full h-full transition-transform duration-75"
              style={{
                transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
                transformOrigin: 'center center',
              }}
            >
              <img 
                src={capturedImage} 
                alt="Captured" 
                className="w-full h-full object-cover touch-none"
                draggable={false}
              />
              
              {/* Crop overlay */}
              {!detecting && renderCropOverlay()}
              
              {/* Corner handles - optimized for touch */}
              {!detecting && corners.map((corner, index) => {
                const isActive = draggingCorner === index;
                const displayX = corner.x * displayScale + imageOffset.x;
                const displayY = corner.y * displayScale + imageOffset.y;
                const clampedX = containerSize.width ? Math.max(0, Math.min(containerSize.width, displayX)) : displayX;
                const clampedY = containerSize.height ? Math.max(0, Math.min(containerSize.height, displayY)) : displayY;
                return (
                  <div
                    key={index}
                    className="absolute z-10"
                    style={{
                      left: clampedX,
                      top: clampedY,
                      width: HANDLE_HIT_AREA / zoomLevel,
                      height: HANDLE_HIT_AREA / zoomLevel,
                      transform: 'translate(-50%, -50%)',
                    }}
                    onPointerDown={(e) => handlePointerDown(index, e)}
                    onPointerMove={handlePointerMove}
                  >
                    {/* Visual handle */}
                    <div 
                      className={`absolute rounded-full border-[3px] transition-transform duration-100 ${
                        isActive 
                          ? 'bg-primary border-white scale-110' 
                          : 'bg-white border-primary'
                      } shadow-lg`}
                      style={{
                        width: HANDLE_SIZE / zoomLevel,
                        height: HANDLE_SIZE / zoomLevel,
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                      }}
                    >
                      {/* Inner circle */}
                      <div 
                        className={`absolute rounded-full ${isActive ? 'bg-white' : 'bg-primary'}`}
                        style={{
                          width: 12 / zoomLevel,
                          height: 12 / zoomLevel,
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Magnifier bubble - positioned smartly */}
            {draggingCorner !== null && magnifierPos && (
              <div 
                className="absolute z-20 pointer-events-none"
                style={getMagnifierStyle()}
              >
                <div 
                  className="rounded-full overflow-hidden border-3 border-primary shadow-xl bg-black"
                  style={{ width: MAGNIFIER_SIZE, height: MAGNIFIER_SIZE }}
                >
                  <canvas 
                    ref={magnifierCanvasRef}
                    width={MAGNIFIER_SIZE}
                    height={MAGNIFIER_SIZE}
                    className="w-full h-full"
                  />
                </div>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" size="lg" onClick={cancelEdit} disabled={detecting} className="h-12">
              <X className="w-5 h-5 shrink-0" />
              <span>Annuler</span>
            </Button>
            <Button size="lg" onClick={confirmCrop} disabled={detecting || corners.length !== 4} className="h-12">
              {detecting ? (
                <Loader2 className="w-5 h-5 shrink-0 animate-spin" />
              ) : (
                <Check className="w-5 h-5 shrink-0" />
              )}
              <span>Valider</span>
            </Button>
          </div>
        </div>
      )}

      {/* Filter selection step */}
      {isFiltering && croppedPreview && (
        <div className="space-y-4">
          <div className="text-center text-sm font-medium py-2 px-4 rounded-lg bg-primary/10 text-primary inline-flex items-center gap-2 mx-auto">
            <Palette className="w-4 h-4" />
            Choisissez un filtre
          </div>
          
          {/* Preview with selected filter */}
          <div className="relative bg-muted rounded-2xl overflow-hidden aspect-[3/4]">
            <img 
              src={croppedPreview} 
              alt="Cropped preview" 
              className="w-full h-full object-contain"
              style={{
                filter: selectedFilter === "original" 
                  ? "none" 
                  : selectedFilter === "bw" 
                    ? "grayscale(100%) contrast(1.4) brightness(1.05)"
                    : selectedFilter === "auto"
                      ? "contrast(1.3) brightness(1.1)"
                      : "contrast(1.6) brightness(1.15)"
              }}
            />
          </div>
          
          {/* Filter options */}
          <div className="grid grid-cols-4 gap-2">
            {(["original", "auto", "bw", "highContrast"] as FilterType[]).map((filter) => (
              <button
                key={filter}
                onClick={() => setSelectedFilter(filter)}
                className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                  selectedFilter === filter
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  selectedFilter === filter ? "bg-primary text-white" : "bg-muted"
                }`}>
                  {filter === "original" && <Image className="w-5 h-5" />}
                  {filter === "auto" && <Sun className="w-5 h-5" />}
                  {filter === "bw" && <Contrast className="w-5 h-5" />}
                  {filter === "highContrast" && <Palette className="w-5 h-5" />}
                </div>
                <span className="text-xs font-medium">{getFilterLabel(filter)}</span>
              </button>
            ))}
          </div>
          
          <div className="grid grid-cols-4 gap-2">
            <Button variant="outline" size="lg" onClick={cancelFilter} disabled={detecting} className="h-12 flex flex-col items-center justify-center gap-0.5 px-1">
              <X className="w-4 h-4 shrink-0" />
              <span className="text-[10px]">Annuler</span>
            </Button>
            <Button variant="outline" size="lg" onClick={backToCrop} disabled={detecting || !originalCapturedImage} className="h-12 flex flex-col items-center justify-center gap-0.5 px-1">
              <RotateCcw className="w-4 h-4 shrink-0" />
              <span className="text-[10px]">Manuel</span>
            </Button>
            <Button variant="outline" size="lg" onClick={autoRecrop} disabled={detecting || !originalCapturedBlob} className="h-12 flex flex-col items-center justify-center gap-0.5 px-1">
              {detecting ? (
                <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
              ) : (
                <Crop className="w-4 h-4 shrink-0" />
              )}
              <span className="text-[10px]">Auto-rogner</span>
            </Button>
            <Button size="lg" onClick={confirmFilter} disabled={detecting} className="h-12 flex flex-col items-center justify-center gap-0.5 px-1">
              {detecting ? (
                <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
              ) : (
                <Check className="w-4 h-4 shrink-0" />
              )}
              <span className="text-[10px]">Valider</span>
            </Button>
          </div>
        </div>
      )}

      {/* Initial state - no pages, not capturing */}
      {!isEditing && !isFiltering && !isCapturing && pages.length === 0 && (
        <div className="space-y-4">
          <button
            className="w-full aspect-[3/4] rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 hover:bg-primary/10 hover:border-primary/50 transition-all flex flex-col items-center justify-center gap-4"
            onClick={startCamera}
          >
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
              <Camera className="w-10 h-10 text-primary" />
            </div>
            <div className="text-center">
              <p className="text-lg font-medium">Prendre une photo</p>
              <p className="text-sm text-muted-foreground">Cadrez votre document</p>
            </div>
          </button>

          {cameraError && (
            <p className="text-sm text-destructive text-center">{cameraError}</p>
          )}

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">ou</span>
            </div>
          </div>

          <button
            className="w-full py-4 rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:bg-primary/5 transition-colors flex items-center justify-center gap-3"
            onClick={() => document.getElementById('scanner-image-input')?.click()}
          >
            <Image className="w-5 h-5 text-muted-foreground" />
            <span className="font-medium">Importer une image</span>
          </button>
          <input
            id="scanner-image-input"
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* Live camera view with OpenCV real-time detection */}
      {isCapturing && !isEditing && !isFiltering && (
        <Suspense fallback={
          <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
            <Loader2 className="w-12 h-12 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">{t.pdf.initializingScanner}</p>
          </div>
        }>
          <OpenCVScanner
            onCapture={async (blob, corners) => {
              setIsCapturing(false);

              // Store original blob for auto-recrop feature
              setOriginalCapturedBlob(blob);

              // Load image for processing
              const url = URL.createObjectURL(blob);
              const img = new window.Image();
              img.onload = async () => {
                imageRef.current = img;
                setImageSize({ width: img.width, height: img.height });

                // Also store as originalCapturedImage for backToCrop
                revokeBlobUrl(capturedImage);
                revokeBlobUrl(originalCapturedImage);
                setOriginalCapturedImage(url);

                if (corners && corners.length === 4) {
                  // Apply perspective correction with detected corners
                  try {
                    const correctedBlob = await applyPerspectiveCorrection(img, corners);
                    const correctedUrl = URL.createObjectURL(correctedBlob);
                    setCroppedBlob(correctedBlob);
                    setCroppedPreview(correctedUrl);
                    setIsFiltering(true);
                  } catch (err) {
                    console.error('Perspective correction failed:', err);
                    // Fallback to manual cropping
                    setCapturedImage(url);
                    const detectedCorners = detectDocumentEdges(img);
                    setCorners(detectedCorners);
                    setIsEditing(true);
                  }
                } else {
                  // No corners detected, use manual cropping
                  setCapturedImage(url);
                  const detectedCorners = detectDocumentEdges(img);
                  setCorners(detectedCorners);
                  setIsEditing(true);
                }
              };
              img.src = url;
            }}
            onCancel={() => setIsCapturing(false)}
          />
        </Suspense>
      )}

      {/* Multi-page view with scanned pages */}
      {!isEditing && !isFiltering && !isCapturing && pages.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold">
              {pages.length} page{pages.length > 1 ? 's' : ''}
            </span>
            <Button variant="ghost" size="sm" onClick={resetAll} className="text-destructive hover:text-destructive">
              <Trash2 className="w-4 h-4 mr-1" />
              Effacer tout
            </Button>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {pages.map((page, index) => (
              <div 
                key={page.id} 
                className="relative group aspect-[3/4] rounded-xl overflow-hidden border-2 border-border bg-muted shadow-sm"
              >
                <img 
                  src={page.preview} 
                  alt={`Page ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                
                <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs font-bold px-2 py-1 rounded-full">
                  {index + 1}
                </div>
                
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {index > 0 && (
                    <Button 
                      variant="secondary" 
                      size="icon" 
                      className="h-10 w-10"
                      onClick={() => movePage(index, index - 1)}
                    >
                      ←
                    </Button>
                  )}
                  <Button 
                    variant="destructive" 
                    size="icon" 
                    className="h-10 w-10"
                    onClick={() => removePage(page.id)}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                  {index < pages.length - 1 && (
                    <Button 
                      variant="secondary" 
                      size="icon" 
                      className="h-10 w-10"
                      onClick={() => movePage(index, index + 1)}
                    >
                      →
                    </Button>
                  )}
                </div>
              </div>
            ))}
            
            {canAddMorePages ? (
              <button
                className="aspect-[3/4] rounded-xl border-2 border-dashed border-primary/30 hover:border-primary/50 hover:bg-primary/5 transition-all flex flex-col items-center justify-center gap-2"
                onClick={startCamera}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Camera className="w-6 h-6 text-primary" />
                </div>
                <span className="text-sm font-medium text-primary">Ajouter</span>
              </button>
            ) : (
              <div className="aspect-[3/4] rounded-xl border-2 border-dashed border-muted-foreground/30 bg-muted/50 flex flex-col items-center justify-center gap-2 p-2">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <Camera className="w-6 h-6 text-muted-foreground" />
                </div>
                <span className="text-xs font-medium text-muted-foreground text-center">
                  Limite {maxPagesPerDoc} pages
                </span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3 pt-2">
            <Button 
              variant="outline"
              size="lg"
              onClick={downloadAsPdf}
              disabled={processing || saving}
              className="h-14 flex flex-col items-center justify-center gap-1 px-2"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="text-xs">Télécharger</span>
            </Button>
            <Button 
              size="lg"
              onClick={saveAsDocument}
              disabled={processing || saving}
              className="h-14 flex flex-col items-center justify-center gap-1 px-2"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              <span className="text-xs">Sauvegarder</span>
            </Button>
            <Button 
              variant="outline"
              size="lg"
              onClick={async () => {
                if (pages.length === 0) return;
                setProcessing(true);
                try {
                  const { PDFDocument } = await import('pdf-lib');
                  const pdfDoc = await PDFDocument.create();
                  for (const page of pages) {
                    const imageBytes = await page.blob.arrayBuffer();
                    const image = await pdfDoc.embedJpg(imageBytes);
                    const pdfPage = pdfDoc.addPage([image.width, image.height]);
                    pdfPage.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
                  }
                  const pdfBytes = await pdfDoc.save();
                  const pdfBlob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
                  const file = new File([pdfBlob], 'scan.pdf', { type: 'application/pdf' });
                  
                  // Try native share first
                  if (navigator.share && navigator.canShare({ files: [file] })) {
                    await navigator.share({ files: [file], title: 'Document scanné' });
                  } else {
                    // Fallback: save document and copy share link
                    const { data: { user } } = await supabase.auth.getUser();
                    if (!user) {
                      toast.error("Connectez-vous pour partager");
                      return;
                    }
                    
                    const fileName = `scan_${Date.now()}.pdf`;
                    const filePath = `${user.id}/${fileName}`;
                    
                    // Upload to storage
                    const { error: uploadError } = await supabase.storage
                      .from('documents')
                      .upload(filePath, pdfBlob, { contentType: 'application/pdf' });
                    
                    if (uploadError) throw uploadError;
                    
                    // Call validate-document instead of direct insert
                    const { data: validateResult, error: validateError } = await supabase.functions.invoke('validate-document', {
                      body: {
                        fileName,
                        fileSize: pdfBlob.size,
                        mimeType: 'application/pdf',
                        filePath,
                        source: 'upload',
                        userId: user.id,
                        title: 'Document scanné',
                        sourceMetadata: {
                          scanner_pages: pages.length,
                          scanner_type: 'web_share',
                        },
                      },
                    });

                    if (validateError) throw validateError;

                    if (!validateResult?.success || validateResult?.result === 'rejected') {
                      await supabase.storage.from('documents').remove([filePath]);
                      toast.error(validateResult?.reason || "Document rejeté");
                      return;
                    }
                    
                    // Show permission dialog before creating share link
                    setPendingShareData({ 
                      docId: validateResult.documentId, 
                      userId: user.id, 
                      doc: { 
                        id: validateResult.documentId,
                        file_name: fileName,
                        mime_type: 'application/pdf',
                        doc_type: 'other',
                        file_path: filePath,
                      }
                    });
                    setShowPermissionDialog(true);
                  }
                } catch (error) {
                  console.error('Share error:', error);
                  toast.error("Erreur lors du partage");
                } finally {
                  setProcessing(false);
                }
              }}
              disabled={processing || saving}
              className="h-14 flex flex-col items-center justify-center gap-1 px-2"
            >
              <Share2 className="w-5 h-5" />
              <span className="text-xs">Partager</span>
            </Button>
          </div>
        </div>
      )}

      {/* Share Dialog */}
      <Dialog open={!!shareDialogUrl} onOpenChange={(open) => !open && setShareDialogUrl(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Lien de partage</DialogTitle>
            <DialogDescription>
              Partagez ce lien pour donner accès à votre document.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {/* Permission level indicator */}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border">
              {selectedPermission === 'read' ? (
                <>
                  <Eye className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Lecture seule</span>
                </>
              ) : (
                <>
                  <MessageSquare className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Lecture + Commentaires</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Input 
                readOnly 
                value={shareDialogUrl || ''} 
                className="text-xs"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
            </div>
            <div className="flex gap-2">
              <Button
                className="flex-1"
                onClick={async () => {
                  if (!shareDialogUrl) return;
                  try {
                    await navigator.clipboard.writeText(shareDialogUrl);
                    toast.success("Lien copié !");
                    setShareDialogUrl(null);
                  } catch {
                    // Fallback
                    const textArea = document.createElement('textarea');
                    textArea.value = shareDialogUrl;
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-9999px';
                    document.body.appendChild(textArea);
                    textArea.select();
                    try {
                      document.execCommand('copy');
                      toast.success("Lien copié !");
                      setShareDialogUrl(null);
                    } catch {
                      toast.error("Sélectionnez et copiez le lien manuellement");
                    }
                    document.body.removeChild(textArea);
                  }
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copier le lien
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  if (shareDialogUrl) {
                    window.open(shareDialogUrl, '_blank');
                  }
                }}
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Permission Choice Dialog */}
      <Dialog open={showPermissionDialog} onOpenChange={(open) => {
        if (!open) {
          setShowPermissionDialog(false);
          setPendingShareData(null);
          setProcessing(false);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Options de partage</DialogTitle>
            <DialogDescription>
              Choisissez les permissions pour ce lien de partage.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <RadioGroup 
              value={selectedPermission} 
              onValueChange={(value) => setSelectedPermission(value as 'read' | 'read_comment')}
            >
              <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => setSelectedPermission('read')}>
                <RadioGroupItem value="read" id="read" className="mt-0.5" />
                <div className="flex-1">
                  <Label htmlFor="read" className="flex items-center gap-2 cursor-pointer font-medium">
                    <Eye className="w-4 h-4 text-muted-foreground" />
                    Lecture seule
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Le destinataire peut uniquement visualiser le document.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => setSelectedPermission('read_comment')}>
                <RadioGroupItem value="read_comment" id="read_comment" className="mt-0.5" />
                <div className="flex-1">
                  <Label htmlFor="read_comment" className="flex items-center gap-2 cursor-pointer font-medium">
                    <MessageSquare className="w-4 h-4 text-muted-foreground" />
                    Lecture + Commentaires
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Le destinataire peut visualiser et laisser des commentaires.
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setShowPermissionDialog(false);
                setPendingShareData(null);
                setProcessing(false);
              }}
            >
              Annuler
            </Button>
            <Button
              className="flex-1"
              disabled={processing}
              onClick={async () => {
                if (!pendingShareData) return;
                
                try {
                  // Create share link with selected permission
                  const { data: shareLink, error: shareError } = await supabase
                    .from('share_links')
                    .insert({
                      document_id: pendingShareData.docId,
                      user_id: pendingShareData.userId,
                      permission: selectedPermission
                    })
                    .select()
                    .single();
                  
                  if (shareError) throw shareError;
                  
                  // Show share dialog with the URL
                  const shareUrl = getShareUrl(shareLink.token);
                  
                  setShowPermissionDialog(false);
                  setShareDialogUrl(shareUrl);
                  
                  // Trigger AI analysis
                  supabase.functions.invoke('extract-document-info', {
                    body: { 
                      fileName: pendingShareData.doc.file_name,
                      mimeType: pendingShareData.doc.mime_type,
                      docType: pendingShareData.doc.doc_type,
                      filePath: pendingShareData.doc.file_path
                    }
                  });
                  
                  setPendingShareData(null);
                } catch (error) {
                  console.error('Share error:', error);
                  toast.error("Erreur lors de la création du lien");
                } finally {
                  setProcessing(false);
                }
              }}
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Créer le lien
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentScanner;
